import { useMutation, useQuery } from "@tanstack/react-query";
import ErrorMsg from "components/admin/UI/ErrorMessage";
import EmptyPlaceHolderGuest from "components/guest-questionnaire/empty-placeholder";
import GuestConfirmationSettings from "components/guest-questionnaire/guest-confirmation-settings";
import RegisterGuestUser from "components/guest-questionnaire/register-guest-user";
import QuestionerGuestSurveyPage from "components/guest-questionnaire/render-survey-form";
import Loader from "components/UI/Loader";
import { CACHE_KEYS } from "constants/common";
import React, { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  createGuestUser,
  getActiveUserTypeQuestioner,
} from "services/api/user.service";
import { showMutationError } from "utils/common";

const STEPS = {
  ONBOARDING: "onboarding",
  SURVEY: "survey",
  POST_SURVEY: "post_survey",
};

const GuestQuestionnaire = () => {
  const params = useParams();
  const userType = params.userType;
  const { data, isLoading, isError } = useQuery({
    queryKey: [CACHE_KEYS.QUESTIONER, "GUEST", userType],
    queryFn: () => {
      return getActiveUserTypeQuestioner(userType);
    },
    enabled: !!userType,
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const { mutateAsync: registerGuestUser, isPending: pendingRegistration } =
    useMutation({
      mutationFn: createGuestUser,
    });

  const [activeStep, setActiveStep] = React.useState(STEPS.ONBOARDING);
  const [createdUser, setCreatedUser] = React.useState(null);

  useEffect(() => {
    const stepParam = searchParams.get("step");
    if (stepParam && Object.values(STEPS).includes(stepParam)) {
      setActiveStep(stepParam);
    }
  }, [searchParams]);

  const handleSubmitForm = async (formData) => {
    try {
      const response = await registerGuestUser({
        ...formData,
        usertype: userType,
      });
      setCreatedUser(response.data);
      setActiveStep(STEPS.SURVEY);
      setSearchParams({ step: STEPS.SURVEY });
    } catch (error) {
      showMutationError(error);
    }
  };

  const handleChangeStep = (step) => {
    setActiveStep(step);
  };

  //CONTENT RENDERING LOGIC
  if (isError) {
    return <ErrorMsg error={"Failed to load questionnaire."} />;
  }

  if (isLoading) {
    return <Loader />;
  }

  const questioner = data?.data;

  if (!questioner) {
    return <EmptyPlaceHolderGuest />;
  }

  return (
    <>
      {activeStep === STEPS.ONBOARDING && (
        <RegisterGuestUser
          userType={userType}
          handleSubmitForm={handleSubmitForm}
          loading={pendingRegistration}
        />
      )}
      {activeStep === STEPS.SURVEY &&
        (createdUser ? (
          <QuestionerGuestSurveyPage
            questioner={questioner}
            progress={{}}
            user={createdUser}
            handleChangeStep={() => {
              handleChangeStep(STEPS.POST_SURVEY);
              setSearchParams({ step: STEPS.POST_SURVEY });
            }}
          />
        ) : (
          //fallback in case of direct access
          <RegisterGuestUser loading={pendingRegistration} handleSubmitForm={handleSubmitForm} />
        ))}
      {activeStep === STEPS.POST_SURVEY &&
        (createdUser ? (
          <GuestConfirmationSettings userType={userType} />
        ) : (
          //fallback in case of direct access
          <RegisterGuestUser loading={pendingRegistration} handleSubmitForm={handleSubmitForm} />
        ))}
    </>
  );
};

export default GuestQuestionnaire;
